import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useAssetsApi } from "../api/AssetsApi";
import { resetError, setError, setLoading } from "../slices/assetLibrarySlice";
import { MediaInfo } from "../types";

export const useAssetService = () => {
  const dispatch = useDispatch();

  const api = useAssetsApi();

  const createAndUploadAsset = useCallback(
    async (companyId: string, file: File, name: string): Promise<MediaInfo> => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ upload: true }));

        // The view reports failures itself, with an upload-specific message.
        const media = await api.uploadMedia(file, companyId, { name, skipGenericToast: true });

        return { ...media, name: media.name ?? name };
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ upload: false }));
      }
    },
    [dispatch, api],
  );

  const updateAsset = useCallback(async (companyId: string, assetId: string, name: string): Promise<void> => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ update: true }));

      await api.updateAsset(companyId, assetId, name);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ update: false }));
    }
  }, [api, dispatch]);

  const deleteAsset = useCallback(async (companyId: string, assetId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ delete: true }));

      await api.deleteAsset(companyId, assetId);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ delete: false }));
    }
  }, [api, dispatch]);

  return {
    createAndUploadAsset,
    updateAsset,
    deleteAsset,
  };
}; 